import React from 'react';
import { useIntl } from 'react-intl';
import { cx } from '@linaria/core';
import { styled } from '@linaria/react';
import { SizesType } from 'types/Sizes';
import { CurrencyDisplayVariant, formatAbbreviatedPriceValue, formatPriceValue } from '@daedalus/core/src/price/business/formatPrice';
import { RoundingStrategy } from '@daedalus/core/src/utils/number';
import { cssTheme } from '../../../themes';
import { Lock } from '../helpers/Lock';
import { Icon } from '../Icon';
export type PriceSize = SizesType;
export type LockSizeType = Extract<SizesType, 'sm' | 'md'>;
interface Props {
  /** Pass through classname to allow styles overrides */
  className?: string;
  /** The currency code for the price */
  currency?: string;
  /** Whether the price should be styled as an anchor price */
  isAnchor?: boolean;
  /** Whether the price is hidden as it is locked */
  isLocked?: boolean;
  /** Whether the price is a private deal */
  isPrivate?: boolean;
  /** Whether the price is an app lock deal */
  isAppLocked?: boolean;
  /** Price maximum decimal places */
  maximumFractionDigits?: number;
  /** Price minimum decimal places */
  minimumFractionDigits?: number;
  /** Whether the price should not be styled */
  noStyle?: boolean;
  /** How the price should be rounded  */
  roundingStrategy?: RoundingStrategy;
  /** Whether the locked icon can be shown. It is only shown when isPrivate is also true. */
  showLockIcon?: boolean;
  /** Whether the app locked icon can be shown. It is only shown when isAppLocked is also true. */
  showAppIcon?: boolean;
  /** Price size */
  size?: PriceSize;
  /** The value to be displayed as the price */
  value: number;
  /** Whether the price should be displayed in offer */
  shouldAlignOffer?: boolean;
  /** Lock size */
  lockSize?: LockSizeType;
  /** When true it will abbreviate the prices to max 6 characters, e.g. €12.3k or €123M. Note that k and M are not translated */
  isAbbreviated?: boolean;
  /** When true it will abbreviate the currency symbol if it exists in ShortCurrencySymbols, otherwise it will not show the currency symbol. This only works if isAbbreviated is also true */
  useShortCurrency?: boolean;
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string;
  /** How to display the currency in currency formatting. i.e. 'symbol', 'narrowSymbol', 'code', 'name', */
  currencyDisplayVariant?: CurrencyDisplayVariant;
  /** Style prop is used by Linaria to pass CSS variables in cases when we need to style custom components in this way: `const StyledPrice = styled(Price)` */
  style?: React.CSSProperties;
}
const textStyles: Record<PriceSize, string> = {
  xs: "xg1nk0a",
  sm: "s14vajsl",
  md: "mmuejx6",
  lg: "l1h9xtm3",
  xl: "x5povwd",
  xxl: "x71njze"
};
const StandardPriceElement = /*#__PURE__*/styled('div')({
  name: "StandardPriceElement",
  class: "s1uq9xur",
  propsAsIs: false
});
const AnchorPriceElement = /*#__PURE__*/styled('div')({
  name: "AnchorPriceElement",
  class: "a1cfho7d",
  propsAsIs: false
});
const Currency = /*#__PURE__*/styled('div')({
  name: "Currency",
  class: "c1xrrwad",
  propsAsIs: false
});
const PriceWrapper = /*#__PURE__*/styled('div')({
  name: "PriceWrapper",
  class: "pf8vbym",
  propsAsIs: false
});
export const Price = ({
  className,
  isAnchor = false,
  value,
  currency,
  noStyle = false,
  minimumFractionDigits = 0,
  maximumFractionDigits = 0,
  isPrivate = false,
  roundingStrategy,
  isLocked = false,
  isAppLocked = false,
  showLockIcon = true,
  showAppIcon = false,
  size = 'lg',
  shouldAlignOffer = false,
  lockSize = 'md',
  isAbbreviated = false,
  useShortCurrency = false,
  dataId = '',
  currencyDisplayVariant,
  style
}: Props) => {
  const {
    locale
  } = useIntl();
  const formattedNumber = isAbbreviated ? formatAbbreviatedPriceValue({
    locale,
    roundingStrategy,
    value,
    currency,
    useShortCurrency,
    currencyDisplayVariant
  }) : formatPriceValue({
    locale,
    roundingStrategy,
    value,
    currency,
    minimumFractionDigits,
    maximumFractionDigits,
    currencyDisplayVariant
  });
  if (isAnchor) {
    return <AnchorPriceElement className={cx(className)} data-id={dataId} translate="no" style={style}>
        {formattedNumber}
      </AnchorPriceElement>;
  }
  return <PriceWrapper className={cx(className, !!shouldAlignOffer && '--withMarginBottom')} data-id={dataId} style={style}>
      {isPrivate && value && showLockIcon && !isAppLocked && <Lock isLocked={isLocked} size={lockSize === 'sm' ? 12 : 16} color={isPrivate ? cssTheme.colors.content.special.c500 : undefined} />}
      {isAppLocked && value && showAppIcon && <Icon name="Iphone" size="sm" colorPath="content.special.c500" />}
      {noStyle ? <Currency translate="no">{formattedNumber}</Currency> : <StandardPriceElement isPrivate={isPrivate} isAppLocked={isAppLocked} className={cx(textStyles[size], (isPrivate || isAppLocked) && '--special')} translate="no">
          {formattedNumber}
        </StandardPriceElement>}
    </PriceWrapper>;
};

require("./Price.wyw-in-js.css!=!../../../../../../node_modules/@wyw-in-js/webpack-loader/lib/outputCssLoader.js?cacheProvider=!./Price.tsx");